import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PlatformAvatar from '../components/platform/PlatformAvatar';
import PlatformButton from '../components/platform/PlatformButton';
import PlatformField from '../components/platform/PlatformField';
import PlatformIcon from '../components/platform/PlatformIcon';
import '../styles/platform-dashboard.css';

export default function SettingsPage() {
  const { user, company } = useSelector((state) => state.auth || {});
  const [tab, setTab] = useState('organization');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copyButtonText, setCopyButtonText] = useState('Copy link');

  const displayName = user?.displayName || user?.name || user?.firstName || 'Admin';
  const companyId = user?.companyID || company?.companyID;
  const inviteLink = companyId
    ? `https://www.nurturer.ai/register/${companyId}`
    : 'https://www.nurturer.ai/register';

  useEffect(() => {
    setError('');
    setMessage('');
  }, [tab]);

  const handleCopyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopyButtonText('Copied');
      setTimeout(() => setCopyButtonText('Copy link'), 2500);
    } catch (copyError) {
      setError('Could not copy invite link.');
    }
  };

  const handlePasswordSubmit = (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!password || !confirmPassword) {
      setError('Please enter and confirm your new password.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setMessage('Password updated successfully.');
      setPassword('');
      setConfirmPassword('');
    }, 1200);
  };

  const tabs = [
    ['organization', 'Organization'],
    ['security', 'Security'],
  ];

  return (
    <div className="nurturer-platform">
      <div className="settings-view platform-fade">
        {error && <div className="platform-alert platform-alert--error">{error}</div>}
        {message && <div className="platform-alert platform-alert--success">{message}</div>}

        <div className="platform-row settings-tabs">
          {tabs.map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`settings-tab ${tab === id ? 'active' : ''}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'organization' && (
          <div className="platform-card settings-card">
            <div className="platform-row" style={{ gap: 18, marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--border)' }}>
              <PlatformAvatar name={displayName} size={72} photoUrl={user?.photoUrl} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>{displayName}</div>
                <div style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 4 }}>
                  {user?.email || '—'}
                  {company?.companyName ? ` · ${company.companyName}` : ''}
                </div>
              </div>
            </div>

            <PlatformField label="Invite link">
              <div className="settings-invite-row">
                <input className="platform-input" value={inviteLink} readOnly />
                <PlatformButton variant="primary" size="md" className="settings-copy-btn" onClick={handleCopyInvite}>
                  {copyButtonText}
                </PlatformButton>
              </div>
            </PlatformField>

            <p style={{ fontSize: 13.5, color: 'var(--muted)', margin: 0, lineHeight: 1.5 }}>
              Share this link so new nurturer users can register under your organization.
            </p>
          </div>
        )}

        {tab === 'security' && (
          <form onSubmit={handlePasswordSubmit} className="platform-card settings-card" style={{ maxWidth: 520 }}>
            <div className="platform-row" style={{ gap: 8, marginBottom: 18 }}>
              <PlatformIcon name="settings" size={18} />
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>Reset password</h3>
            </div>

            <PlatformField label="New password">
              <input
                className="platform-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </PlatformField>

            <PlatformField label="Confirm password">
              <input
                className="platform-input"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </PlatformField>

            <PlatformButton variant="primary" size="md" type="submit" disabled={isLoading}>
              {isLoading ? 'Updating…' : 'Update password'}
            </PlatformButton>
          </form>
        )}
      </div>
    </div>
  );
}
